import type {DefinitionOrReference} from '@github-ui/code-nav'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import {CommandKeybindingHint, GlobalCommands} from '@github-ui/ui-commands'
import {ChevronDownIcon, ChevronUpIcon, SearchIcon, XIcon} from '@primer/octicons-react'
import {IconButton, TextInput} from '@primer/react'
import {clsx} from 'clsx'
import {lazy, Suspense, useEffect, useRef, useState} from 'react'

import {useSearchTerm, useSetSearchTerm} from '../../../../contexts/SearchTermContext'
import {useIsCursorEnabled} from '../../../../hooks/use-cursor-navigation'
import {scrollLineIntoView} from '../../../../hooks/use-scroll-line-into-view'
import styles from './FindInFilePopover.module.css'

const ScrollMarks = lazy(() => import('./ScrollMarks'))

export default function FindInFilePopover({
  stickied,
  searchResults,
  focusedSearchResult,
  setFocusedSearchResult,
  onClose,
}: {
  stickied?: boolean
  focusedSearchResult: number | undefined
  setFocusedSearchResult: (idx: number | undefined) => void
  searchResults: DefinitionOrReference[]
  onClose: () => void
}) {
  const searchTerm = useSearchTerm()
  const setSearchTerm = useSetSearchTerm()
  const cursorEnabled = useIsCursorEnabled()
  const inputContainer = useRef<HTMLInputElement>(null)

  const [escapeToBrowserOnNextKeyPress, setEscapeToBrowserOnNextKeyPress] = useState(true)
  const clearSearch = () => {
    setSearchTerm('')
    setFocusedSearchResult(0)
  }
  const {sendRepoKeyDownEvent} = useReposAnalytics()

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value) {
      setEscapeToBrowserOnNextKeyPress(false)
      setSearchTerm(event.target.value)

      if (focusedSearchResult === undefined) {
        setFocusedSearchResult(0)
      }
    } else {
      setEscapeToBrowserOnNextKeyPress(true)
      clearSearch()
    }
  }

  const jumpToResult = (direction: number) => {
    if (focusedSearchResult === undefined) {
      setFocusedSearchResult(0)
      return
    }

    if (direction === 1) {
      setFocusedSearchResult(focusedSearchResult === searchResults.length - 1 ? 0 : focusedSearchResult + 1)
    } else {
      setFocusedSearchResult(focusedSearchResult === 0 ? searchResults.length - 1 : focusedSearchResult - 1)
    }
  }

  useEffect(() => {
    inputContainer.current?.focus()
    inputContainer.current?.select()
  }, [])

  const onHotkey = () => {
    const selected = window.getSelection()?.toString()
    if (selected?.length) {
      setSearchTerm(selected)
      setFocusedSearchResult(0)
      sendRepoKeyDownEvent('BLOB_FIND_IN_FILE_MENU.FIND_IN_FILE_FROM_SELECTION')
    }
    inputContainer.current?.focus()
    inputContainer.current?.select()
  }

  useEffect(() => {
    if (searchResults.length > 0 && focusedSearchResult !== undefined) {
      scrollLineIntoView({
        line: searchResults[focusedSearchResult]!.lineNumber,
        column: searchResults[focusedSearchResult]!.ident.start.column,
      })
    }
  }, [searchResults, focusedSearchResult])

  if (cursorEnabled) return null

  return (
    <div
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className={`find-in-file-popover ${
        stickied ? 'find-in-file-popover-stickied' : 'find-in-file-popover-not-stickied'
      }`}
    >
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <h5 className={styles.findHeading}>Find</h5>
          <span
            className={clsx(
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              'find-text-help-tooltip',
              styles.helpText,
              !escapeToBrowserOnNextKeyPress && styles.helpTextHidden,
            )}
          >
            Press <CommandKeybindingHint commandId="code-view:find-in-file" /> again to open the browser&apos;s find
            menu
          </span>
        </div>
        <div className={styles.spacer} />
        <IconButton
          variant="invisible"
          size="small"
          onClick={onClose}
          icon={XIcon}
          className={styles.closeButton}
          aria-label="Close find in file"
        />
      </div>
      <div className={styles.inputWrapper}>
        <TextInput
          ref={inputContainer}
          className={styles.textInput}
          validationStatus={searchResults.length > 1000 ? 'error' : undefined}
          type="text"
          leadingVisual={() => <SearchIcon aria-hidden="true" />}
          aria-labelledby="find-in-file-label"
          aria-expanded="true"
          autoComplete="off"
          name="Find in file input"
          placeholder="Search this file"
          value={searchTerm}
          block
          onChange={onChange}
          trailingAction={
            <div className={styles.trailingActions}>
              <span className={clsx('text-small', styles.resultCount)}>
                {searchResults.length === 0 || focusedSearchResult === undefined ? 0 : focusedSearchResult + 1}/
                {searchResults.length}
              </span>
              <IconButton
                size="small"
                variant="invisible"
                onClick={() => {
                  jumpToResult(-1)
                }}
                icon={ChevronUpIcon}
                aria-label="Up"
                data-testid="up-search"
                className={styles.navButton}
              />
              <IconButton
                size="small"
                variant="invisible"
                onClick={() => {
                  jumpToResult(1)
                }}
                icon={ChevronDownIcon}
                aria-label="Down"
                data-testid="down-search"
                className={styles.navButton}
              />
            </div>
          }
          onKeyDown={(keyboardEvent: React.KeyboardEvent) => {
            if (keyboardEvent.code === 'Enter' || keyboardEvent.code === 'NumpadEnter') {
              if (keyboardEvent.shiftKey) {
                jumpToResult(-1)
              } else {
                jumpToResult(1)
              }
            } else if (
              (keyboardEvent.metaKey || keyboardEvent.ctrlKey) &&
              (keyboardEvent.key === 'g' || keyboardEvent.key === 'G')
            ) {
              keyboardEvent.preventDefault()
              if (keyboardEvent.shiftKey) {
                jumpToResult(-1)
              } else {
                jumpToResult(1)
              }
            } else if (
              (keyboardEvent.metaKey || keyboardEvent.ctrlKey) &&
              (keyboardEvent.key === 'f' || keyboardEvent.key === 'F')
            ) {
              if (escapeToBrowserOnNextKeyPress) {
                // The user wants to use the browser's find in page functionality.
                // So just close this pane and get it out of their way.
                sendRepoKeyDownEvent('BLOB_FIND_IN_FILE_MENU.FALLBACK_TO_BROWSER_SEARCH')
                onClose()
              } else {
                setEscapeToBrowserOnNextKeyPress(true)
                keyboardEvent.preventDefault()
                inputContainer.current?.focus()
                inputContainer.current?.select()
              }
            } else if (keyboardEvent.key === 'Escape') {
              onClose()
            }
          }}
        />

        <GlobalCommands
          commands={{
            'code-view:find-in-file': onHotkey,
            'code-view:find-selection': onHotkey,
            'code-view:find-next': () => jumpToResult(1),
            'code-view:find-prev': () => jumpToResult(-1),
          }}
        />
        <Suspense fallback={null}>
          <ScrollMarks definitionsOrReferences={searchResults} />
        </Suspense>
      </div>
    </div>
  )
}
